import './join-event.css';
import { Table } from 'antd';
import { calculateAge } from '@utils/date-utils';
import { Athlete, DIVISION_STYLE, STATUS } from '@type/db-types';
import {
    Alert,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from '@mui/material';
import useLatestCallback from 'use-latest-callback';
import { useCallback, useMemo } from 'react';
import { GENDER_LOOKUP } from '@utils/team-table-utils';
import NestedTabNavigation from '@atoms/NestedTabNavigation';
import RegisteredAthletesTable from './RegisteredAthletesTable';
import useEventContext from '@hooks/useEventContext';
import MadisonSystemInfoAlert from '@atoms/MadisonSystemInfoAlert';
import { getClassLabel, getDivisionLabel } from '@utils/event-utils';

export interface WrestlerDivision {
    athleteId: string;
    divisionId: string;
}

export interface WrestlerWeightClass {
    athleteId: string;
    weightClassId: string;
}

interface Props {
    athletes: Athlete[];
    athletesDivision: WrestlerDivision[];
    athletesWeightClass: WrestlerWeightClass[];
    loading: boolean;
    selectedRows: Athlete[];
    setSelectedRows: React.Dispatch<React.SetStateAction<Athlete[]>>;
    setAthletesDivision: (division: WrestlerDivision) => void;
    setAthletesWeightClass: (weightClass: WrestlerWeightClass) => void;
}

export default function JoinEventTable({
    athletes,
    loading,
    selectedRows,
    setSelectedRows,
    setAthletesDivision,
    athletesDivision,
    athletesWeightClass,
    setAthletesWeightClass,
}: Props) {
    const { event, isEventAdmin } = useEventContext();
    const handleWrestlersDivisionChange = useLatestCallback(
        (divisionId: string, record: Athlete) => {
            const divisionObj: WrestlerDivision = {
                athleteId: record.id,
                divisionId: divisionId,
            };
            setAthletesDivision(divisionObj);
        },
    );

    const handleWrestlersWeightClassChange = useLatestCallback(
        (weightClassId: string, record: Athlete) => {
            const weightClassObj: WrestlerWeightClass = {
                athleteId: record.id,
                weightClassId: weightClassId,
            };
            setAthletesWeightClass(weightClassObj);
        },
    );

    const rowSelection = useMemo(
        () => ({
            // type: 'checkbox',
            selectedRowKeys: selectedRows.map((row) => row.id),
            onChange: (
                // @ts-expect-error value is not used
                selectedRowKeys,
                selectedRows: Athlete[],
            ) => {
                setSelectedRows(selectedRows);
            },
            // getCheckboxProps: (record: Athlete) => ({
            //     disabled: athletes?.find((athlete) => athlete.id === record.id),
            // }),
        }),
        [selectedRows, setSelectedRows],
    );

    const columns = useMemo(
        () => [
            {
                title: 'Wrestler Name',
                dataIndex: 'name',
                key: 'name',
                render: (
                    // @ts-expect-error value is not used
                    value,
                    record: Athlete,
                ) => {
                    return (
                        <>
                            {record?.firstName} {record?.lastName}
                        </>
                    );
                },
            },
            {
                dataIndex: 'gender',
                key: 'gender',
                lookup: GENDER_LOOKUP,
                render: (value: string) => (
                    <span className="text-capitalize">{value ? value : ''}</span>
                ),
                title: 'Gender',
                width: '6em',
            },
            {
                title: 'Age',
                dataIndex: 'birthday',
                key: 'birthday',
                render: (value: string) => {
                    return <>{calculateAge(value)}</>;
                },
                width: '5em',
            },
            {
                title: 'Weight',
                dataIndex: 'weight',
                key: 'weight',
                width: '6em',
            },
            {
                title: 'Select Division',
                dataIndex: 'division',
                key: 'division',
                render: (
                    // @ts-expect-error value is not used
                    value,
                    record: Athlete,
                ) => {
                    const selectedDivision = athletesDivision.find(
                        ({ athleteId }) => athleteId === record.id,
                    );

                    return (
                        <FormControl sx={{ minWidth: '10em' }} size="small">
                            <InputLabel id={`division-select-label-${record.id}`}>
                                Division
                            </InputLabel>
                            <Select
                                onClick={(e) => e.stopPropagation()}
                                id={`division-select-${record.id}`}
                                label="Select division"
                                labelId={`division-select-label-${record.id}`}
                                native={false}
                                onChange={(value) => {
                                    // console.log('Select division value', value, child);
                                    handleWrestlersDivisionChange(value.target.value, record);
                                }}
                                renderValue={(value) => {
                                    const div = event?.divisions.find((div) => div.id === value);
                                    return (
                                        <Typography variant="body2">
                                            {getDivisionLabel(div)}
                                        </Typography>
                                    );
                                }}
                                value={selectedDivision?.divisionId || ''}
                            >
                                {event?.divisions?.map((division) => (
                                    <MenuItem key={division.id} value={division.id}>
                                        <Typography variant="body2">
                                            {getDivisionLabel(division)}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                },
            },
            {
                title: 'Select Weight Class',
                dataIndex: 'weightClass',
                key: 'weightClass',
                render: (
                    // @ts-expect-error value is not used
                    value,
                    record: Athlete,
                ) => {
                    const selectedDivision = athletesDivision.find(
                        ({ athleteId }) => athleteId === record.id,
                    )?.divisionId;
                    const selectedClass = athletesWeightClass.find(
                        ({ athleteId }) => athleteId === record.id,
                    );
                    const div = event?.divisions.find((div) => div.id === selectedDivision);

                    return (
                        <FormControl sx={{ minWidth: '10em' }} size="small">
                            {selectedDivision ? (
                                div?.bracketStyle === DIVISION_STYLE.MADISON_SYSTEM ? (
                                    <MadisonSystemInfoAlert />
                                ) : (
                                    <Box>
                                        <InputLabel id={`class-select-label-${record.id}`}>
                                            Weight Class
                                        </InputLabel>
                                        <Select
                                            onClick={(e) => e.stopPropagation()}
                                            id={`class-select-${record.id}`}
                                            label="Select class"
                                            labelId={`class-select-label-${record.id}`}
                                            native={false}
                                            sx={{ minWidth: '10em' }}
                                            onChange={(value) =>
                                                handleWrestlersWeightClassChange(
                                                    value.target.value,
                                                    record,
                                                )
                                            }
                                            renderValue={(value) => {
                                                const curClass = event?.divisions
                                                    ?.find(
                                                        ({ id }) =>
                                                            id ===
                                                            athletesDivision.find(
                                                                ({ athleteId }) =>
                                                                    athleteId === record.id,
                                                            )?.divisionId,
                                                    )
                                                    ?.classes.find((cls) => cls.id === value);
                                                return (
                                                    <Typography variant="body2">
                                                        {curClass ? getClassLabel(curClass) : ''}
                                                    </Typography>
                                                );
                                            }}
                                            value={selectedClass?.weightClassId || ''}
                                        >
                                            {event?.divisions
                                                ?.find(
                                                    ({ id }) =>
                                                        id ===
                                                        athletesDivision.find(
                                                            ({ athleteId }) =>
                                                                athleteId === record.id,
                                                        )?.divisionId,
                                                )
                                                ?.classes.map((c) => (
                                                    <MenuItem key={c.id} value={c.id}>
                                                        <Typography variant="body2">
                                                            {getClassLabel(c)}
                                                        </Typography>
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </Box>
                                )
                            ) : (
                                <Tooltip title="Select a Division first!">
                                    <Alert
                                        severity="warning"
                                        variant="outlined"
                                        sx={{ paddingY: 0 }}
                                    >
                                        <Typography variant="caption">
                                            Select Division first!
                                        </Typography>
                                    </Alert>
                                    {/* <Stack alignItems="center">
                                    <ReportProblemIcon />
                                    <Typography variant="caption" color="warning">
                                        select division
                                    </Typography>
                                </Stack> */}
                                </Tooltip>
                            )}
                        </FormControl>
                    );
                },
            },
        ],
        [
            athletesDivision,
            athletesWeightClass,
            event,
            handleWrestlersDivisionChange,
            handleWrestlersWeightClassChange,
        ],
    );

    const toggleRowSelection = useCallback(
        (record: Athlete) =>
            setSelectedRows((prev) => {
                const index = prev.findIndex((row) => row.id === record.id);
                if (index > -1) {
                    return prev.filter((row) => row.id !== record.id);
                } else {
                    return [...prev, record];
                }
            }),
        [setSelectedRows],
    );

    const { unregistered, registered } = useMemo(
        (): { unregistered: Athlete[]; registered: Athlete[] } =>
            athletes.reduce(
                (acc: { unregistered: Athlete[]; registered: Athlete[] }, athlete) => {
                    if (event?.memberships?.find((m) => m.athleteIds.includes(athlete.id))) {
                        acc.registered.push(athlete);
                    } else {
                        acc.unregistered.push(athlete);
                    }
                    return acc;
                },
                { unregistered: [] as Athlete[], registered: [] as Athlete[] },
            ),
        [athletes, event],
    );

    const tabs = useMemo(() => {
        return [
            ...(unregistered.length
                ? [
                      {
                          tabName: 'New',
                          renderFn: () => (
                              <div className="table-wrapper mt-1 w-100">
                                  <Table
                                      rowSelection={rowSelection}
                                      onRow={(record /* rowIndex */) => {
                                          return {
                                              onClick: () => toggleRowSelection(record), // click row
                                          };
                                      }}
                                      dataSource={unregistered}
                                      columns={columns}
                                      rowKey="id"
                                      scroll={{ y: 'calc(100vh - 500px)' }}
                                      rowClassName={'cursor-pointer'}
                                      loading={loading}
                                      // pagination={{
                                      //     current: defaultCurrent,
                                      //     total: wrestlers?.wrestlersData?.total || 0,
                                      //     onChange: (page, pageSize) => {
                                      //         setDefaultCurrent(page);
                                      //         const offset = pageSize * (page - 1);
                                      //         fetchWrestler(offset, pageSize);
                                      //     },
                                      //     showSizeChanger: false,
                                      // }}
                                  />
                              </div>
                          ),
                      },
                  ]
                : []),
            ...(registered.length
                ? [
                      {
                          tabName: 'Already Registered',
                          renderFn: () =>
                              event ? (
                                  <RegisteredAthletesTable
                                      event={event}
                                      adminView={isEventAdmin}
                                      title={unregistered.length ? undefined : 'Already Registered'}
                                      statuses={[STATUS.ACTIVE]}
                                  />
                              ) : null,
                      },
                  ]
                : []),
        ];
    }, [
        columns,
        event,
        isEventAdmin,
        loading,
        registered.length,
        rowSelection,
        toggleRowSelection,
        unregistered,
    ]);

    return (
        <NestedTabNavigation
            doNotNavigateURL
            tabBoxProps={{
                paddingLeft: 0,
                paddingTop: tabs.length && tabs.length > 1 ? 0.5 : 0,
            }}
            tabs={tabs}
            sx={{ marginTop: 1, marginLeft: 0.1 }}
        />
    );
}
